import { getTitle, getName, DATASETS } from '@/components/Constants';
import { formatEvidenceNamedList } from '@/utils/formatEvidenceNamedList';
import { formatGtpList } from '@/utils/formatGtpList';

export interface SearchResultsProps {
  state: any;
  dispatch: any;
}

export type ScoreHistoryPanelState = {
  selectedYear: number | null;
  selectedYearFrom: number | null;
  selectedYearTo: number | null;
  yearInput: string;
  yearFromInput: string;
  yearToInput: string;
  tab?: string;
};

const SCORE_HISTORY_FALLBACK_MIN_YEAR = 1900;

export function getRowReferenceYearBounds(row: Record<string, unknown> | null | undefined): {
  minYear: number;
  maxYear: number;
} {
  const calendarYear = new Date().getFullYear();

  const yearInitial = Number(row?.yearInitial);
  const yearFinal = Number(row?.yearFinal);
  if (Number.isFinite(yearInitial) && Number.isFinite(yearFinal) && !(yearInitial === 0 && yearFinal === 0)) {
    return { minYear: Math.trunc(yearInitial), maxYear: Math.trunc(yearFinal) };
  }

  const firstRef = Number(row?.firstRef);
  const lastRef = Number(row?.lastRef);
  if (Number.isFinite(firstRef) && Number.isFinite(lastRef) && !(firstRef === 0 && lastRef === 0)) {
    return { minYear: Math.trunc(firstRef), maxYear: Math.trunc(lastRef) };
  }

  return { minYear: SCORE_HISTORY_FALLBACK_MIN_YEAR, maxYear: calendarYear };
}

export type DetailGroup = {
  title: string;
  entries: Array<{ key: string; value: any; displayLabel: string }>;
};

export const CATEGORY_ORDER = ['GDA', 'VDA', 'DDA', 'Gene', 'Disease', 'Variant', 'Chemical', 'Relation'];

export const ROW_DETAIL_CONTEXT_LABELS: Record<string, string> = {
  GDA: 'Gene - disease',
  VDA: 'Variant - disease',
  DDA: 'Disease - disease',
  DISEASES: 'Disease detail',
  GENES: 'Gene detail',
  VARIANTS: 'Variant detail',
  CHEMICALS: 'Chemical detail',
};

export const SHOW_HISTORIAL_SCORE = process.env.REACT_APP_SHOW_HISTORIAL_SCORE === 'true';
export const RIGHT_SIDEBAR_COLLAPSED_WIDTH_PX = 52;

export type BottomDetailTab = 'summary' | 'evidence' | 'historical' | 'gtp' | 'canopy';
export const HISTORICAL_SCORE_PEEK_HEIGHT = 248;
export const HISTORICAL_SCORE_COMPACT_MAX_HEIGHT = 360;

export function historicalScoreSupportsClinicalTrials(tab: string, view?: string): boolean {
  const normalizedTab = String(tab ?? '').toUpperCase();
  const normalizedView = String(view ?? '').toUpperCase();
  if (normalizedTab === 'VDA' || normalizedTab === 'VARIANTS') return false;
  if (normalizedView === 'VARIANTS') return false;
  return normalizedTab === 'GDA';
}

export function getSearchItemBadgeLabel(item: any): { label: string; tooltipId: string } {
  const title = getTitle(item);
  const id = getName(item);
  const titleStr =
    typeof title === 'object' && title !== null && 'title' in title
      ? String((title as { title?: string }).title ?? id)
      : String(title ?? '');
  const idStr = String(id ?? item?.id ?? '');
  const isAllDataset =
    typeof title === 'object' &&
    title !== null &&
    'title' in (title as object) &&
    (title as { title?: string }).title === DATASETS.ALL;
  const label = titleStr && !isAllDataset ? titleStr : idStr;
  return { label, tooltipId: idStr };
}

export function getNumericScoreFromSearchItem(item: any): number | null {
  const keys = ['score', 'matchScore', 'relevance', 'relevanceScore'] as const;
  for (const k of keys) {
    const v = item?.[k];
    if (v == null || v === '') continue;
    const n = typeof v === 'number' ? v : parseFloat(String(v));
    if (Number.isFinite(n)) return n;
  }
  return null;
}

export function sortSearchItemsForDisplay(items: any[]): any[] {
  if (!items?.length) return [];
  const decorated = items.map(item => ({ item, ...getSearchItemBadgeLabel(item) }));
  const anyScore = decorated.some(d => getNumericScoreFromSearchItem(d.item) != null);
  if (anyScore) {
    return [...decorated]
      .sort((a, b) => {
        const sa = getNumericScoreFromSearchItem(a.item);
        const sb = getNumericScoreFromSearchItem(b.item);
        if (sa != null && sb != null) return sb - sa;
        if (sa != null) return -1;
        if (sb != null) return 1;
        return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
      })
      .map(d => d.item);
  }
  return [...decorated]
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
    .map(d => d.item);
}

export function getEntityPluralLabel(view: string | undefined, items: any[]): string {
  const byDataset: Record<string, string> = {
    DISEASES: 'diseases',
    GENES: 'genes',
    VARIANTS: 'variants',
    CHEMICALS: 'chemicals',
  };
  if (view && view !== 'ALL') return byDataset[view] ?? 'items';
  const datasets = new Set(items.map(i => i?.dataset).filter(Boolean) as string[]);
  if (datasets.size === 1) {
    const only = [...datasets][0];
    return byDataset[only] ?? 'items';
  }
  return 'items';
}

export function humanizeKey(key: string): string {
  const normalized = key.toLowerCase();
  if (normalized === 'firstref') return 'First Reference';
  if (normalized === 'lastref') return 'Last Reference';
  if (normalized === 'pmid') return 'PMID';
  if (normalized === 'gtp' || normalized === 'gtap') return 'GTAP';
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, c => c.toUpperCase());
}

function getExpandedDetailLabel(label: string, key: string): string {
  const normalizedLabel = label.trim().toLowerCase().replace(/\./g, '').replace(/\s+/g, ' ');
  const normalizedKey = key
    .trim()
    .toLowerCase()
    .replace(/[_\s-]/g, '');

  const byLabel: Record<string, string> = {
    'first ref': 'First Reference',
    'last ref': 'Last Reference',
    'n. pmids': 'Number of PMIDs',
    'n pmids': 'Number of PMIDs',
    'n. pmids chemicals': 'Number of PMIDs Mentioning Chemicals',
    'n pmids chemicals': 'Number of PMIDs Mentioning Chemicals',
    'n variants gda': 'Number of Variants in the GDA',
    'ei gda': 'Evidence Index of the GDA',
    'n cts': 'Number of Clinical Trials',
    'n cts chemicals': 'Number of Chemicals in the Clinical Trials',
  };
  if (byLabel[normalizedLabel]) return byLabel[normalizedLabel];

  const byKey: Record<string, string> = {
    firstref: 'First Reference',
    lastref: 'Last Reference',
    numpmids: 'Number of PMIDs',
    numpmidschemicals: 'Number of PMIDs Mentioning Chemicals',
    nvariantsgda: 'Number of Variants in the GDA',
    eigda: 'Evidence Index of the GDA',
    numcts: 'Number of Clinical Trials',
    numctschemicals: 'Number of Chemicals in the Clinical Trials',
  };
  if (byKey[normalizedKey]) return byKey[normalizedKey];

  return label;
}

export function normalizeValue(value: any, key?: string): string {
  if (value === null || value === undefined || value === '') return '-';
  if (key === 'disease_model' || key === 'disease_model_in_doc' || key === 'sex' || key === 'sex_in_doc') {
    return formatEvidenceNamedList(value);
  }
  if (key === 'GTP' || key === 'gtp' || key === 'GTAP' || key === 'gtap') {
    return formatGtpList(value);
  }
  if (key === 'chemsIncludedInEvidenceBySource') {
    if (Array.isArray(value)) return `${value.length} sources`;
    if (typeof value === 'object') return `${Object.keys(value).length} sources`;
  }
  if (Array.isArray(value)) {
    if (!value.length) return '-';
    const hasObjects = value.some(item => typeof item === 'object' && item !== null);
    if (hasObjects) return `${value.length} items`;
    return value.map(item => String(item)).join(', ');
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value);
    if (!entries.length) return '-';
    const preview = entries
      .slice(0, 4)
      .map(([entryKey, entryValue]) => `${humanizeKey(entryKey)}: ${normalizeValue(entryValue)}`)
      .join(' | ');
    return entries.length > 4 ? `${preview} | ...` : preview;
  }
  return String(value);
}

function isPliKey(key?: string): boolean {
  if (!key) return false;
  const normalizedKey = key.replace(/[_\s]/g, '').toLowerCase();
  return normalizedKey === 'pli' || normalizedKey === 'genepli';
}

function formatDetailNumericValue(value: number, key?: string): string {
  if (!Number.isFinite(value)) return String(value);
  if (isPliKey(key)) return Number.parseFloat(String(value)).toExponential(4);
  const roundedTwoDecimalPlaces = Math.round((value + Number.EPSILON) * 100) / 100;
  if (Number.isInteger(roundedTwoDecimalPlaces)) return String(roundedTwoDecimalPlaces);
  let text = roundedTwoDecimalPlaces.toFixed(2);
  text = text.replace(/(\.\d*?)0+$/, '$1');
  text = text.replace(/\.$/, '');
  return text;
}

export const DETAIL_ROW_EXCLUDED_KEYS = new Set(['text', 'GTP', 'gtp', 'GTAP', 'gtap']);

function isVariantPositionDetailField(key: string, displayLabel?: string): boolean {
  if (key.toLowerCase() === 'coord') return true;
  return (displayLabel ?? '').trim().toLowerCase() === 'position';
}

function isDetailRowExcludedKey(key: string, displayLabel?: string, category?: string): boolean {
  if (DETAIL_ROW_EXCLUDED_KEYS.has(key)) return true;
  if (key.toLowerCase() === 'coord') return true;
  if (category === 'Variant' && isVariantPositionDetailField(key, displayLabel)) return true;
  return false;
}

export function isSentenceHtmlDetailKey(key?: string): boolean {
  if (!key) return false;
  const normalized = key.toLowerCase().replace(/[_-]/g, '');
  return normalized === 'sentencehtml' || normalized === 'pmedtitle';
}

export function normalizeDetailValue(value: any, key?: string): string {
  if (value === null || value === undefined || value === '') return '-';
  if (key === 'disease_model' || key === 'disease_model_in_doc' || key === 'sex' || key === 'sex_in_doc') {
    return formatEvidenceNamedList(value);
  }
  if (key === 'GTP' || key === 'gtp' || key === 'GTAP' || key === 'gtap') {
    return formatGtpList(value);
  }

  if (typeof value === 'number') {
    return formatDetailNumericValue(value, key);
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    const numericValue = Number(trimmed);
    if (trimmed !== '' && Number.isFinite(numericValue)) {
      return formatDetailNumericValue(numericValue, key);
    }
  }

  if (Array.isArray(value)) {
    if (!value.length) return '-';
    const hasObjects = value.some(item => typeof item === 'object' && item !== null);
    if (hasObjects) return `${value.length} items`;
    return value.map(item => normalizeDetailValue(item, key)).join(', ');
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value);
    if (!entries.length) return '-';
    const preview = entries
      .slice(0, 4)
      .map(
        ([entryKey, entryValue]) => `${humanizeKey(entryKey)}: ${normalizeDetailValue(entryValue, entryKey)}`,
      )
      .join(' | ');
    return entries.length > 4 ? `${preview} | ...` : preview;
  }

  return String(value);
}

export function countItems(value: any): number {
  if (value === null || value === undefined || value === '') return 0;
  if (Array.isArray(value)) return value.filter(Boolean).length;
  if (typeof value === 'number') return Number.isFinite(value) ? Math.max(0, value) : 0;
  if (typeof value === 'string') {
    const numericValue = Number(value);
    if (!Number.isNaN(numericValue) && value.trim() !== '') return Math.max(0, numericValue);
    return value
      .split(',')
      .map(item => item.trim())
      .filter(Boolean).length;
  }
  return 1;
}

export function getRowValueByPriority(
  row: Record<string, any> | null | undefined,
  exactKeys: string[],
  includesKeys: string[] = [],
): any {
  if (!row || typeof row !== 'object') return undefined;
  for (const key of exactKeys) {
    if (row[key] !== undefined && row[key] !== null && row[key] !== '') return row[key];
  }
  if (!includesKeys.length) return undefined;
  const entries = Object.entries(row);
  for (const includeKey of includesKeys) {
    const found = entries.find(([k, v]) => {
      const valuePresent = v !== undefined && v !== null && v !== '';
      return valuePresent && k.toLowerCase().includes(includeKey.toLowerCase());
    });
    if (found) return found[1];
  }
  return undefined;
}

export function getRowDetailContextLabel(tab: string | undefined): string {
  if (tab == null || String(tab).trim() === '') return 'Association';
  const k = String(tab).toUpperCase();
  return ROW_DETAIL_CONTEXT_LABELS[k] ?? tab;
}

export function getBottomDetailHeader(
  bottomDetailTab: BottomDetailTab,
  relationSummary: {
    leftEntity: string;
    rightEntity: string;
    showRelationArrow: boolean;
  },
  defaultContextLabel: string,
  row?: Record<string, any> | null,
): {
  contextLabel: string;
  primaryEntity: string;
  secondaryEntity: string;
  showRelationArrow: boolean;
} {
  if (bottomDetailTab === 'gtp') {
    const rawGene = getRowValueByPriority(row, ['geneSymbol', 'gene_symbol', 'geneName', 'symbol'], ['gene']);
    return {
      contextLabel: 'Gene',
      primaryEntity: rawGene != null && rawGene !== '' ? normalizeValue(rawGene) : relationSummary.leftEntity,
      secondaryEntity: '',
      showRelationArrow: false,
    };
  }

  if (bottomDetailTab === 'canopy') {
    const rawDisease = getRowValueByPriority(
      row,
      ['diseaseName', 'disease_name', 'disease1_Name', 'disease1Name', 'diseaseId'],
      ['disease'],
    );
    return {
      contextLabel: 'Diseases',
      primaryEntity:
        rawDisease != null && rawDisease !== ''
          ? normalizeValue(rawDisease)
          : relationSummary.rightEntity || relationSummary.leftEntity,
      secondaryEntity: '',
      showRelationArrow: false,
    };
  }

  return {
    contextLabel: defaultContextLabel,
    primaryEntity: relationSummary.leftEntity,
    secondaryEntity: relationSummary.rightEntity,
    showRelationArrow: relationSummary.showRelationArrow,
  };
}

export function groupRowDetails(
  row: any,
  columnLabels: Record<string, string> | null | undefined = undefined,
  columnCategories: Record<string, string> | null | undefined = undefined,
  currentTab?: string,
): DetailGroup[] {
  if (!row || typeof row !== 'object') return [];
  const normalizedTab = String(currentTab ?? '').toUpperCase();
  const labelFor = (key: string) => {
    const rawLabel = columnLabels?.[key] ?? humanizeKey(key);
    return getExpandedDetailLabel(rawLabel, key);
  };
  const groups: Record<string, Array<{ key: string; value: any; displayLabel: string }>> = {};
  const ensureGroup = (cat: string) => {
    if (!groups[cat]) groups[cat] = [];
  };

  if (columnCategories && Object.keys(columnCategories).length > 0) {
    Object.entries(row).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') return;
      let cat = columnCategories[key];
      if (normalizedTab === 'VDA' && cat === 'Gene') {
        cat = 'Variant';
      }
      if (!cat) return;
      const displayLabel = labelFor(key);
      if (['id', 'dataset'].includes(key.toLowerCase()) || isDetailRowExcludedKey(key, displayLabel, cat)) {
        return;
      }
      ensureGroup(cat);
      groups[cat].push({ key, value, displayLabel });
    });
  } else {
    const keys = Object.keys(row).map(k => k.toLowerCase());
    const hasVariant = keys.some(k => k.includes('variant') || k.includes('dbsnp') || k.includes('hgv'));
    const diseaseLikeCount = keys.filter(k => k.includes('disease')).length;
    const inferredAssoc = hasVariant ? 'VDA' : diseaseLikeCount > 1 ? 'DDA' : 'GDA';

    Object.entries(row).forEach(([key, value]) => {
      const lower = key.toLowerCase();
      if (value === null || value === undefined || value === '') return;
      const displayLabel = labelFor(key);

      const pushDetailEntry = (groupTitle: string) => {
        if (['id', 'dataset'].includes(lower) || isDetailRowExcludedKey(key, displayLabel, groupTitle)) {
          return;
        }
        ensureGroup(groupTitle);
        groups[groupTitle].push({ key, value, displayLabel });
      };

      if (lower === 'coreaction' || lower.includes('relation')) {
        pushDetailEntry('Relation');
      } else if (lower.includes('gene')) {
        pushDetailEntry(normalizedTab === 'VDA' ? 'Variant' : 'Gene');
      } else if (lower.includes('disease')) {
        pushDetailEntry('Disease');
      } else if (lower.includes('chem')) {
        pushDetailEntry('Chemical');
      } else if (
        lower.includes('variant') ||
        lower.includes('hgv') ||
        lower.includes('dbsnp') ||
        lower.includes('allele')
      ) {
        pushDetailEntry('Variant');
      } else {
        pushDetailEntry(inferredAssoc);
      }
    });
  }

  if ((currentTab || '').toUpperCase() !== 'CHEMICALS') {
    delete groups.Chemical;
  }

  const knownOrder = CATEGORY_ORDER.filter(cat => groups[cat]?.length);
  const extra = Object.keys(groups).filter(cat => !CATEGORY_ORDER.includes(cat) && groups[cat].length);
  return [...knownOrder, ...extra.sort()].map(title => ({ title, entries: groups[title] }));
}
